import { useEffect, useState, type ReactNode } from 'react';
import { deleteReplayFrames, replayStorage } from '@/session/db.js';
import { Button } from '@/ui/primitives.js';

/**
 * Replays on this phone (Settings → Your data). Frames leave on their own 7
 * days after each session; this deletes them sooner. The reports stay — their
 * summaries are in the account and the ten-minute cards are kept.
 * Two taps, no system dialog (dialogs block the native webview's events).
 */
export function ReplayStorage(): ReactNode {
  const [usage, setUsage] = useState<{ sessions: number; bytes: number } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void replayStorage()
      .then(setUsage)
      .catch(() => setUsage(null));
  }, []);

  const remove = async (): Promise<void> => {
    setBusy(true);
    try {
      await deleteReplayFrames();
      setUsage({ sessions: 0, bytes: 0 });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (!usage) return null;
  const mb = usage.bytes / 1_000_000;

  return (
    <div className="space-y-2 rounded-xl bg-surface p-3">
      <p className="text-sm">
        Replays on this phone:{' '}
        <strong>
          {usage.sessions === 0
            ? 'none'
            : `${usage.sessions} session${usage.sessions === 1 ? '' : 's'} · ${mb < 1 ? '<1' : mb.toFixed(0)} MB`}
        </strong>
      </p>
      <p className="text-xs text-muted">
        The joint positions behind each session's replay, kept for 7 days after each session. They
        never leave this phone unless you share keypoint data.
      </p>
      {usage.sessions > 0 ? (
        confirming ? (
          <div className="flex gap-2">
            <Button variant="danger" className="flex-1" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Deleting…' : 'Delete all replays'}
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <button type="button" className="text-sm font-semibold underline" onClick={() => setConfirming(true)}>
            Delete replays on this phone
          </button>
        )
      ) : null}
      {confirming ? (
        <p className="text-xs text-muted">Reports, scores and the ten-minute snapshots are kept.</p>
      ) : null}
    </div>
  );
}
